import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { AuthProvider } from '@/contexts/AuthContext'
import { lazy, Suspense } from 'react'

// Critical component - load immediately
import { Hero } from './components/Hero'

// Lazy load components below the fold
const Portfolio = lazy(() => import('./components/Portfolio').then(m => ({ default: m.Portfolio })))
const About = lazy(() => import('./components/About').then(m => ({ default: m.About })))
const Services = lazy(() => import('./components/Services').then(m => ({ default: m.Services })))
const FAQ = lazy(() => import('./components/FAQ').then(m => ({ default: m.FAQ })))

const AIBanner = lazy(() => import('./components/AIBanner').then(m => ({ default: m.AIBanner })))
const Awards = lazy(() => import('./components/Awards').then(m => ({ default: m.Awards })))
const Blog = lazy(() => import('./components/Blog').then(m => ({ default: m.Blog })))
const Contact = lazy(() => import('./components/Contact').then(m => ({ default: m.Contact })))
const Footer = lazy(() => import('./components/Footer').then(m => ({ default: m.Footer })))
const CookieConsent = lazy(() => import('./components/CookieConsent').then(m => ({ default: m.CookieConsent })))
const ChatWidget = lazy(() => import('./components/ChatWidget').then(m => ({ default: m.ChatWidget })))


// Admin pages - lazy loaded
const AdminLogin = lazy(() => import('./pages/admin/Login'))
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'))
const ProtectedRoute = lazy(() => import('./components/admin/ProtectedRoute'))
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))
const BlogManager = lazy(() => import('./pages/admin/BlogManager'))
const PortfolioManager = lazy(() => import('./pages/admin/PortfolioManager'))
const ServicesManager = lazy(() => import('./pages/admin/ServicesManager'))
const LeadsManager = lazy(() => import('./pages/admin/LeadsManager'))
const SettingsManager = lazy(() => import('./pages/admin/SettingsManager'))
const FAQManager = lazy(() => import('./pages/admin/FAQManager'))
const ChatAgentConfig = lazy(() => import('./pages/admin/ChatAgentConfig'))
const BlogAIConfig = lazy(() => import('./pages/admin/BlogAIConfig'))
const ProposalsManager = lazy(() => import('./pages/admin/proposals/ProposalsManager'))
const BlogPost = lazy(() => import('./pages/BlogPost'))
const BlogList = lazy(() => import('./pages/BlogList'))
const AppointmentsCalendar = lazy(() => import('./pages/admin/AppointmentsCalendar'))
const AIAgentsPage = lazy(() => import('./pages/AIAgentsPage'))

const queryClient = new QueryClient()

// Minimal loading fallback
function SectionSkeleton() {
  return (
    <div className="min-h-[200px] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
    </div>
  )
}

function HomePage() {
  return (
    <div className="bg-background text-foreground" style={{ overflowX: 'hidden' }}>
      <main id="main-content" className="relative" role="main" style={{ overflowX: 'hidden' }}>
        <section id="hero" aria-label="Hero section">
          <Hero />
        </section>
        <Suspense fallback={<SectionSkeleton />}>
          <section id="portfolio" aria-label="Portfolio section">
            <Portfolio />
          </section>
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <section id="about" aria-label="About section">
            <About />
          </section>
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <section id="services" aria-label="Services section">
            <Services />
          </section>
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <section id="ai-banner" aria-label="AI Banner section">
            <AIBanner />
          </section>
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <section id="awards" aria-label="Awards section">
            <Awards />
          </section>
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <section id="faq" aria-label="FAQ section">
            <FAQ />
          </section>
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <section id="blog" aria-label="Blog section">
            <Blog />
          </section>
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <section id="contact" aria-label="Contact section">
            <Contact />
          </section>
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<SectionSkeleton />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/agentes-ia" element={<AIAgentsPage />} />
              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="blog" element={<BlogManager />} />
                <Route path="blog-ai" element={<BlogAIConfig />} />
                <Route path="portfolio" element={<PortfolioManager />} />
                <Route path="services" element={<ServicesManager />} />
                <Route path="leads" element={<LeadsManager />} />
                <Route path="faq" element={<FAQManager />} />
                <Route path="proposals" element={<ProposalsManager />} />
                <Route path="settings" element={<SettingsManager />} />
                <Route path="chat-agent" element={<ChatAgentConfig />} />
                <Route path="appointments" element={<AppointmentsCalendar />} />
              </Route>
            </Routes>
          </Suspense>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
